import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import NetworkGraph from "./components/NetworkGraph";
import ControlPanel from "./components/ControlPanel";
import SuspectBehaviorControlPanel from "./components/SuspectBehaviorControlPanel";
import Login from "./components/Login";
import "./App.css";
import DisplayTabs, { TabIdEnum } from "./components/DisplayTabs";
import SuspectTagsPageContent, {
  SuspectTag,
} from "./components/SuspectTagsPageContent";
import SuspectBehaviorPageContent, {
  SuspectPlace,
  SuspectUser,
} from "./components/SuspectBehavior";
import { useAuth0 } from "@auth0/auth0-react";
import { tokenAuthorizationParams } from "./utils/auth0-configs";
import { envVars } from "./utils/envVars";

interface Node {
  id: string;
  size: number;
  type: "system" | "lock";
  name?: string;
  accessPointCount?: number;
}

interface Link {
  source: string;
  target: string;
  weight: number;
  avgTime?: number;
}

interface GraphData {
  nodes: Node[];
  links: Link[];
}

interface System {
  systemId: string;
  systemName: string;
  totalUnlocks: number;
}

const API_BASE_URL = envVars.DASHBOARD_API_BASE_URL;

// Hash-based color generation with guaranteed color separation
const getSystemColor = (systemId: string) => {
  // Create hash from system ID
  let hash = 0;
  for (let i = 0; i < systemId.length; i++) {
    const char = systemId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  // Predefined color palette with good contrast
  const colorPalette = [
    "hsl(220, 90%, 50%)", // Blue
    "hsl(120, 70%, 45%)", // Green
    "hsl(0, 85%, 55%)", // Red
    "hsl(45, 100%, 50%)", // Orange/Yellow
    "hsl(270, 80%, 60%)", // Purple
    "hsl(340, 80%, 55%)", // Pink
    "hsl(180, 70%, 45%)", // Cyan
    "hsl(25, 85%, 55%)", // Orange
    "hsl(300, 70%, 50%)", // Magenta
    "hsl(60, 80%, 45%)", // Olive
    "hsl(200, 80%, 50%)", // Light Blue
    "hsl(140, 60%, 45%)", // Forest Green
    "hsl(15, 90%, 55%)", // Red Orange
    "hsl(280, 75%, 55%)", // Violet
    "hsl(320, 85%, 50%)", // Hot Pink
  ];

  // Use hash to select from palette, with fallback generation for more than 15 systems
  const paletteIndex = Math.abs(hash) % colorPalette.length;

  if (paletteIndex < colorPalette.length) {
    return colorPalette[paletteIndex];
  } else {
    // Fallback for systems beyond the palette
    const hue = (Math.abs(hash) * 137) % 360;
    const saturation = 70 + (Math.abs(hash * 7) % 20);
    const lightness = 40 + (Math.abs(hash * 11) % 20);
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }
};

function App() {
  // Authentication state
  // const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const { isAuthenticated, getAccessTokenSilently, logout } = useAuth0();

  // State management
  const [graphType, setGraphType] = useState<"systems" | "locks">("systems");
  const [selectedSystemId, setSelectedSystemId] = useState<string>("");
  const [timeLimit, setTimeLimit] = useState<number>(60000); // 1 minute default
  const [consecutiveLimit, setConsecutiveLimit] = useState<number>(1);
  const [graphData, setGraphData] = useState<GraphData>({
    nodes: [],
    links: [],
  });
  const [systems, setSystems] = useState<System[]>([]);
  const [suspectTags, setSuspectTags] = useState<SuspectTag[]>([]);
  const [suspectBehaviorUsers, setSuspectBehaviorUsers] = useState<
    SuspectUser[]
  >([]);
  const [suspectBehaviorPlaces, setSuspectBehaviorPlaces] = useState<
    SuspectPlace[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuspectTagsLoading, setIsSuspectTagsLoading] =
    useState<boolean>(false);
  const [isSuspectBehaviorLoading, setIsSuspectBehaviorLoading] =
    useState<boolean>(false);
  const [selectedSuspectBehaviorTarget, setSelectedSuspectBehaviorTarget] =
    useState<"users" | "access-points">("users");

  const [error, setError] = useState<string>("");

  // Graph dimensions
  const [dimensions, setDimensions] = useState({
    width: Math.max(800, window.innerWidth - 400),
    height: Math.max(600, window.innerHeight - 200),
  });

  // tab management
  const [activeTab, setActiveTab] = useState<TabIdEnum>(TabIdEnum.ACCESS_GRAPH);

  // Handle login
  // const handleLogin = (username: string, password: string) => {
  //   // Simple authentication - in production, this should validate against a real backend
  //   const valid = validateCredentials(username, password);
  //   if (valid) {
  //     // setIsAuthenticated(true);
  //     localStorage.setItem("clouseau_authenticated", "true");
  //     localStorage.setItem("clouseau_username", username);
  //   } else {
  //     alert("Invalid username or password. Please try again.");
  //   }
  // };

  // Handle logout
  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    setActiveTab(TabIdEnum.ACCESS_GRAPH);

    // clear all data states when leaving the page
    setGraphData({ nodes: [], links: [] });
    setSystems([]);
    setSuspectTags([]);
    setSuspectBehaviorUsers([]);
    setSuspectBehaviorPlaces([]);
    setSelectedSystemId("");
    setSelectedSuspectBehaviorTarget("users");
    setGraphType("systems");
    setTimeLimit(60000); // Reset to default 1 minute
    setConsecutiveLimit(1);
  };

  // Create color map
  const systemColorMap = React.useMemo(() => {
    const map: { [key: string]: string } = {};
    systems.forEach((system) => {
      map[system.systemId] = getSystemColor(system.systemId);
    });
    return map;
  }, [systems]);

  // Update dimensions on window resize
  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: Math.max(800, window.innerWidth - 400),
        height: Math.max(600, window.innerHeight - 200),
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch available systems on component mount
  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab !== TabIdEnum.ACCESS_GRAPH) return;

    const fetchSystems = async () => {
      try {
        const accessToken = await getAccessTokenSilently({
          authorizationParams: tokenAuthorizationParams,
        });
        const response = await axios.get(`${API_BASE_URL}/systems`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        setSystems(response.data);
      } catch (error) {
        console.error("Error fetching systems:", error);
        setError(
          "Failed to fetch systems. Please check if the backend is running."
        );
      }
    };

    fetchSystems();
  }, [isAuthenticated, activeTab, getAccessTokenSilently]);

  // Fetch graph data
  const fetchGraphData = useCallback(async () => {
    if (!isAuthenticated) return;

    if (activeTab !== TabIdEnum.ACCESS_GRAPH) return;

    if (graphType === "locks" && !selectedSystemId) {
      setGraphData({ nodes: [], links: [] });
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      let url = "";
      const params = new URLSearchParams({
        timeLimit: timeLimit.toString(),
        consecutiveLimit: consecutiveLimit.toString(),
      });

      if (graphType === "systems") {
        url = `${API_BASE_URL}/graph/systems?${params}`;
      } else {
        url = `${API_BASE_URL}/graph/locks/${selectedSystemId}?${params}`;
      }

      const accessToken = await getAccessTokenSilently({
        authorizationParams: tokenAuthorizationParams,
      });

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setGraphData(response.data);
    } catch (error) {
      console.error("Error fetching graph data:", error);
      setError(
        "Failed to fetch graph data. Please check your settings and try again."
      );
      setGraphData({ nodes: [], links: [] });
    } finally {
      setIsLoading(false);
    }
  }, [
    graphType,
    selectedSystemId,
    timeLimit,
    consecutiveLimit,
    isAuthenticated,
    activeTab,
    getAccessTokenSilently,
  ]);

  const fetchSuspectTags = useCallback(async () => {
    if (!isAuthenticated) return;
    if (activeTab !== TabIdEnum.SUSPECT_TAGS) return;

    setIsSuspectTagsLoading(true);
    setError("");

    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: tokenAuthorizationParams,
      });
      const response = await axios.get(`${API_BASE_URL}/suspect/tags`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setSuspectTags(response.data);

      // setSuspectTags(DUMMY_SUSPECT_TAGS_DATA); // Use dummy data for now
    } catch (error) {
      console.error("Error fetching suspect tags:", error);
      setError(
        "Failed to fetch suspect tags. Please check if the backend is running."
      );
    } finally {
      setIsSuspectTagsLoading(false);
    }
  }, [isAuthenticated, activeTab, getAccessTokenSilently]);

  const fetchSuspectBehaviorUsers = useCallback(async () => {
    if (!isAuthenticated) return;
    if (activeTab !== TabIdEnum.SUSPECT_BEHAVIOUR) return;
    if (selectedSuspectBehaviorTarget !== "users") return;

    setIsSuspectBehaviorLoading(true);
    setError("");

    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: tokenAuthorizationParams,
      });
      const response = await axios.get(`${API_BASE_URL}/suspect/users`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setSuspectBehaviorUsers(response.data);
    } catch (error) {
      console.error("Error fetching suspect behavior users:", error);
      setError(
        "Failed to fetch suspect behavior data for users. Please check if the backend is running."
      );
    } finally {
      setIsSuspectBehaviorLoading(false);
    }
  }, [
    isAuthenticated,
    activeTab,
    selectedSuspectBehaviorTarget,
    getAccessTokenSilently,
  ]);

  const fetchSuspectBehaviorPlaces = useCallback(async () => {
    if (!isAuthenticated) return;
    if (activeTab !== TabIdEnum.SUSPECT_BEHAVIOUR) return;
    if (selectedSuspectBehaviorTarget !== "access-points") return;

    setIsSuspectBehaviorLoading(true);
    setError("");

    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: tokenAuthorizationParams,
      });
      const response = await axios.get(`${API_BASE_URL}/suspect/places`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      setSuspectBehaviorPlaces(response.data);
    } catch (error) {
      console.error("Error fetching suspect behavior places:", error);
      setError(
        "Failed to fetch suspect behavior data for access points. Please check if the backend is running."
      );
    } finally {
      setIsSuspectBehaviorLoading(false);
    }
  }, [
    isAuthenticated,
    activeTab,
    selectedSuspectBehaviorTarget,
    getAccessTokenSilently,
  ]);

  // Auto-fetch when parameters change
  useEffect(() => {
    fetchGraphData();
  }, [fetchGraphData]);

  useEffect(() => {
    fetchSuspectTags();
  }, [fetchSuspectTags]);

  useEffect(() => {
    fetchSuspectBehaviorUsers();
  }, [fetchSuspectBehaviorUsers]);

  useEffect(() => {
    fetchSuspectBehaviorPlaces();
  }, [fetchSuspectBehaviorPlaces]);

  // Handle node clicks (for drilling down from systems to locks)
  const handleNodeClick = useCallback(
    (nodeId: string) => {
      if (graphType === "systems") {
        setGraphType("locks");
        setSelectedSystemId(nodeId);
      }
    },
    [graphType]
  );

  // Handle node double clicks
  const handleNodeDoubleClick = useCallback(
    (nodeId: string) => {
      if (graphType === "systems") {
        setGraphType("locks");
        setSelectedSystemId(nodeId);
      }
    },
    [graphType]
  );

  // Control panel handlers
  const handleGraphTypeChange = (type: "systems" | "locks") => {
    setGraphType(type);
    if (type === "systems") {
      setSelectedSystemId("");
    }
  };

  const handleBackToSystems = () => {
    setGraphType("systems");
    setSelectedSystemId("");
  };

  const handleApplyFilters = () => {
    // This function is no longer needed but kept for compatibility
  };

  const handleReset = () => {
    // This function is no longer needed but kept for compatibility
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <div>
          <h1>
            <img
              src="/Clouseau.svg"
              alt="Clouseau logo"
              style={{
                width: "40px",
                height: "40px",
                marginRight: "12px",
              }}
            />
            Clouseau AI Access Network Analyzer
          </h1>
          <p>AI-powered security analytics for building access systems</p>
        </div>
        <div className="header-actions">
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="app-content">
        <div className="sidebar">
          {activeTab === TabIdEnum.ACCESS_GRAPH ? (
            <ControlPanel
              graphType={graphType}
              selectedSystemId={selectedSystemId}
              timeLimit={timeLimit}
              consecutiveLimit={consecutiveLimit}
              systems={systems}
              isLoading={isLoading}
              onGraphTypeChange={handleGraphTypeChange}
              onSystemSelect={setSelectedSystemId}
              onTimeLimitChange={setTimeLimit}
              onConsecutiveLimitChange={setConsecutiveLimit}
              onApplyFilters={handleApplyFilters}
              onReset={handleReset}
            />
          ) : activeTab === TabIdEnum.SUSPECT_BEHAVIOUR ? (
            <SuspectBehaviorControlPanel
              selectedTarget={selectedSuspectBehaviorTarget}
              onTargetChange={setSelectedSuspectBehaviorTarget}
            />
          ) : null}

          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        <div className="main-content">
          <div className="graph-header">
            {activeTab === TabIdEnum.ACCESS_GRAPH && (
              <div className="header-text">
                <h2>
                  {graphType === "locks" && (
                    <span
                      style={{
                        cursor: "pointer",
                        marginRight: "12px",
                        fontSize: "18px",
                        color: "#1a73e8",
                      }}
                      onClick={handleBackToSystems}
                    >
                      ←
                    </span>
                  )}
                  {graphType === "systems"
                    ? `System Network (${graphData.nodes.length} systems, ${graphData.links.length} connections)`
                    : `Access Points - ${selectedSystemId} (${graphData.nodes.length} locks, ${graphData.links.length} movements)`}
                </h2>
                {graphType === "systems" && (
                  <p className="graph-hint-text">
                    Click on a system node to view its access points
                  </p>
                )}
              </div>
            )}
            <DisplayTabs activeTab={activeTab} onChange={setActiveTab} />
          </div>
          {activeTab === TabIdEnum.ACCESS_GRAPH ? (
            <div className="graph-container">
              {isLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading graph data...</p>
                </div>
              ) : graphData.nodes.length > 0 ? (
                <>
                  <NetworkGraph
                    data={graphData}
                    width={dimensions.width}
                    height={dimensions.height}
                    selectedSystemId={selectedSystemId}
                    systemColorMap={systemColorMap}
                    onNodeClick={handleNodeClick}
                    onNodeDoubleClick={handleNodeDoubleClick}
                  />
                </>
              ) : (
                <div className="empty-state">
                  <h3>No Data Available</h3>
                  <p>
                    {graphType === "locks" && !selectedSystemId
                      ? "Please select a system to view its access points network."
                      : "No graph data found with the current filter settings. Try adjusting the time limit or minimum frequency."}
                  </p>
                </div>
              )}
            </div>
          ) : activeTab === TabIdEnum.SUSPECT_TAGS ? (
            <div className="graph-container">
              {isSuspectTagsLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading data...</p>
                </div>
              ) : suspectTags.length > 0 ? (
                <>
                  <SuspectTagsPageContent
                    data={suspectTags}
                    title="Suspect Tags Analysis"
                  />
                </>
              ) : (
                <div className="empty-state">
                  <h3>No Data Available</h3>
                  <p>No suspect tags found.</p>
                </div>
              )}
            </div>
          ) : activeTab === TabIdEnum.SUSPECT_BEHAVIOUR ? (
            <div className="graph-container">
              {isSuspectBehaviorLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading data...</p>
                </div>
              ) : selectedSuspectBehaviorTarget === "users" &&
                suspectBehaviorUsers.length > 0 ? (
                <>
                  <SuspectBehaviorPageContent
                    selectedTarget="users"
                    data={suspectBehaviorUsers}
                  />
                </>
              ) : selectedSuspectBehaviorTarget === "access-points" &&
                suspectBehaviorPlaces.length > 0 ? (
                <>
                  <SuspectBehaviorPageContent
                    selectedTarget="access-points"
                    data={suspectBehaviorPlaces}
                  />
                </>
              ) : (
                <div className="empty-state">
                  <h3>No Data Available</h3>
                  <p>
                    No suspect behavior data found for{" "}
                    {selectedSuspectBehaviorTarget === "users"
                      ? "users"
                      : "access points"}
                    .
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default App;
